import { apiClient } from './client';
import { unwrapList, unwrapItem } from './unwrap';
import { normalizeUser } from './normalizeUser';
import type { MyCompetition, PagedResult, Role, User } from './types';

/**
 * Matches Aperture.Api.Controllers.UsersController exactly:
 *  - GET /users returns the complete, unfiltered list of every user
 *    (SuperAdmin-only) - no query params, no pagination/search/role-filter
 *    support - list() below takes no params and callers filter/paginate
 *    client-side.
 *  - POST /users provisions a brand-new account directly, SuperAdmin-only.
 *    Organizers instead provision judge/competitor accounts by assigning
 *    them to a competition (see api/judges.ts assign / api/competitions.ts
 *    assignParticipant), which creates the account on the fly if needed.
 *  - POST/DELETE /users/{id}/roles only ever grant/revoke SuperAdmin now -
 *    every other capability is scoped membership, not a global role.
 *  - There is no generic update endpoint. PUT /users/me only updates the
 *    caller's own profile; activation status has its own dedicated endpoint
 *    below. DELETE /users/{id} permanently removes the account and is
 *    SuperAdmin-only (see UsersService.DeleteAsync for what that cascades).
 */
export const usersApi = {
  list: () =>
    apiClient.get('/users').then((r) => {
      const page = unwrapList<Parameters<typeof normalizeUser>[0]>(r.data);
      return { ...page, items: page.items.map(normalizeUser) } as PagedResult<User>;
    }),

  get: (id: string) =>
    apiClient.get(`/users/${id}`).then((r) => normalizeUser(unwrapItem<Parameters<typeof normalizeUser>[0]>(r.data))),

  /**
   * GET /users/search?q= - narrow lookup (email, first/last name; every word
   * must match) available to any authenticated user, so an organizer can find
   * an existing account to assign as a judge/competitor without the
   * admin-only list() above. Empty term always returns no results server-side.
   */
  search: (q: string) =>
    apiClient
      .get('/users/search', { params: { q } })
      .then((r) => unwrapList<Parameters<typeof normalizeUser>[0]>(r.data).items.map(normalizeUser)),

  getMe: () =>
    apiClient.get('/users/me').then((r) => normalizeUser(unwrapItem<Parameters<typeof normalizeUser>[0]>(r.data))),

  /** GET /users/me/competitions - every competition the caller manages, judges, or participates in. Powers the competition switcher. */
  myCompetitions: () =>
    apiClient
      .get('/users/me/competitions')
      .then((r) => unwrapList<MyCompetition>(r.data)) as Promise<PagedResult<MyCompetition>>,

  /** PUT /users/me - body is `{ firstName, lastName }` (UpdateUserProfileRequest), updates the caller's own profile only. */
  updateMyProfile: (payload: { firstName: string; lastName: string }) =>
    apiClient
      .put('/users/me', payload)
      .then((r) => normalizeUser(unwrapItem<Parameters<typeof normalizeUser>[0]>(r.data))),

  /** PUT /users/me/email - ChangeEmailRequest(NewEmail, CurrentPassword): changes the caller's login email. */
  changeMyEmail: (payload: { newEmail: string; currentPassword: string }) =>
    apiClient
      .put('/users/me/email', payload)
      .then((r) => normalizeUser(unwrapItem<Parameters<typeof normalizeUser>[0]>(r.data))),

  /** POST /users - CreateUserRequest(Email, FirstName, LastName). SuperAdmin-only; the user gets an email to choose a password. */
  create: (payload: { email: string; firstName: string; lastName: string }) =>
    apiClient
      .post('/users', payload)
      .then((r) => normalizeUser(unwrapItem<Parameters<typeof normalizeUser>[0]>(r.data))),

  /** POST /users/{id}/roles - grants SuperAdmin, the only role left to grant. SuperAdmin-only. */
  addRole: (id: string, role: Role) =>
    apiClient
      .post(`/users/${id}/roles`, { role: ROLE_ORDINAL[role] })
      .then((r) => normalizeUser(unwrapItem<Parameters<typeof normalizeUser>[0]>(r.data))),

  /** DELETE /users/{id}/roles/{role} - revokes SuperAdmin. SuperAdmin-only. */
  removeRole: (id: string, role: Role) =>
    apiClient
      .delete(`/users/${id}/roles/${role}`)
      .then((r) => normalizeUser(unwrapItem<Parameters<typeof normalizeUser>[0]>(r.data))),

  activate: (id: string) => apiClient.post(`/users/${id}/activate`).then((r) => r.data),

  deactivate: (id: string) => apiClient.post(`/users/${id}/deactivate`).then((r) => r.data),

  /** DELETE /users/{id} - permanently deletes the account. Irreversible, SuperAdmin only. */
  remove: (id: string) => apiClient.delete(`/users/${id}`).then((r) => r.data),
};

/** Must match backend/src/Aperture.Domain/Identity/Role.cs enum order exactly - SuperAdmin is the only value left. */
const ROLE_ORDINAL: Record<Role, number> = {
  SuperAdmin: 0,
};
